import { put } from './apiClient';
import {
  DEFAULT_DESTINATION,
  PANTRY_DESTINATION_OPEN,
  destinationOf,
  isStockDestination,
  type VendorInvoiceDestination,
} from '../utils/vendorInvoiceDestination';

export { DEFAULT_DESTINATION, destinationOf, type VendorInvoiceDestination };

/**
 * Where a vendor invoice's cost lands (D95, 2026-10-06). ONE value per invoice:
 *  - `united`  - Ship Chandlery, the default (a row stored before the field = united)
 *  - `pantry`  - on hold: the server refuses it, so the UI shows it disabled and never sends it
 *  - `expense` - a company bill: billed and paid, but no stock, catalogue or PO match
 * The rules are utils/vendorInvoiceDestination.ts (mirrored with the server); this holds the
 * labels, the plain message for each refusal code, and the PUT.
 */

export const DESTINATION_OPTIONS: ReadonlyArray<{ value: VendorInvoiceDestination; label: string; disabled: boolean }> = [
  { value: 'united', label: 'Ship Chandlery', disabled: false },
  { value: 'pantry', label: PANTRY_DESTINATION_OPEN ? 'Pantry' : 'Pantry (on hold)', disabled: !PANTRY_DESTINATION_OPEN },
  { value: 'expense', label: 'Company expense', disabled: false },
];

export function destinationLabel(destination: VendorInvoiceDestination): string {
  if (destination === 'pantry') return 'Pantry';
  return DESTINATION_OPTIONS.find((o) => o.value === destination)?.label ?? 'Ship Chandlery';
}

/** A company expense stocks nothing and is never matched to a purchase order. */
export const isExpenseInvoice = (invoice: { destination?: string | null } | null | undefined) =>
  !isStockDestination(destinationOf(invoice));

/**
 * The one place the screens' stock wording is decided (Confirm button, dialogs, notes):
 * an invoice that stocks says "Inventory", a company expense says what it does instead.
 * Callers read these, they do not branch on the destination themselves.
 */
export interface DestinationWording {
  stocks: boolean;
  confirmButton: string;
  confirmButtonTitle: string;
  confirmedLabel: string;
  confirmedChip: string;
  doneTitle: string;
  doneSummary: string;
  doneHint: string;
  confirmQuestionTitle: (lineCount: number) => string;
  confirmQuestionBody: string;
  confirmQuestionButton: string;
  alreadyTitle: string;
  moneyEditSummary: string;
  vendorLockTitle: string;
}

export function destinationWording(invoice: { destination?: string | null } | null | undefined): DestinationWording {
  const stocks = isStockDestination(destinationOf(invoice));
  return stocks
    ? {
        stocks,
        confirmButton: 'Confirm & sync to Inventory',
        confirmButtonTitle: "Add this invoice's line items to Inventory and the Master Catalogue",
        confirmedLabel: 'Sent to Inventory',
        confirmedChip: '✓ In Inventory',
        doneTitle: 'Sent to Inventory',
        doneSummary: "This invoice's goods are now in stock and the invoice is marked as confirmed.",
        doneHint: 'A later edit to this invoice will not move stock again. Correct stock in Inventory if a quantity needs fixing.',
        confirmQuestionTitle: (n) => `Send ${n} line${n === 1 ? '' : 's'} to Inventory?`,
        confirmQuestionBody: 'Stock moves once. This cannot be undone here.',
        confirmQuestionButton: 'Confirm & sync',
        alreadyTitle: 'This invoice is already in Inventory',
        moneyEditSummary:
          'Stock will not change. The bill in Accounts Payable and the Master Catalogue price will follow what you save.',
        vendorLockTitle:
          'Already sent to Inventory: its catalogue rows, stock and item memory are filed under this vendor, so it cannot be moved here.',
      }
    : {
        stocks,
        confirmButton: 'Confirm expense',
        confirmButtonTitle: 'Confirms this company expense. It goes to payables only: no stock, no catalogue.',
        confirmedLabel: 'Confirmed',
        confirmedChip: '✓ Confirmed',
        doneTitle: 'Confirmed',
        doneSummary: 'This company expense is confirmed and billed. Nothing was added to stock or the catalogue.',
        doneHint: 'This invoice added no stock.',
        confirmQuestionTitle: () => 'Confirm this company expense?',
        confirmQuestionBody: 'It goes to payables only: no stock, no catalogue.',
        confirmQuestionButton: 'Confirm expense',
        alreadyTitle: 'This invoice is already confirmed',
        moneyEditSummary: 'The bill in Accounts Payable will follow what you save.',
        vendorLockTitle: 'Already confirmed: its bill is filed under this vendor, so it cannot be moved here.',
      };
}

const MESSAGES: Record<string, string> = {
  VENDOR_INVOICE_DESTINATION_REQUIRED: 'Choose where this invoice goes: Ship Chandlery or Company expense.',
  VENDOR_INVOICE_DESTINATION_INVALID: 'That destination is not one of the choices.',
  VENDOR_INVOICE_DESTINATION_PANTRY_ON_HOLD: 'Pantry invoices are on hold. Choose Ship Chandlery or Company expense.',
  VENDOR_INVOICE_EXPENSE_HAS_PO: 'This invoice is matched to a purchase order, and a company expense is not. Detach the purchase order first.',
  VENDOR_INVOICE_DESTINATION_LOCKED:
    'This invoice is already confirmed, so its stock, catalogue and bill follow the old destination. Delete the invoice to return its stock, then upload it again.',
  VENDOR_INVOICE_NOT_FOUND: 'This invoice no longer exists.',
  VENDOR_INVOICE_EXPENSE_NO_PO_MATCH: 'A company expense is not matched to a purchase order.',
  VENDOR_INVOICE_EXPENSE_NO_STOCK: 'This invoice is a company expense, so it added no stock and there is nothing to send to a vessel.',
  PACKAGING_CORRECTION_EXPENSE: 'This invoice is a company expense, so its packaging is not tracked.',
};

/** Copy for a destination-related refusal, keyed on the server's `code`, never its prose. */
export function destinationErrorMessage(err: unknown, fallback: string): string {
  const code = (err as { code?: unknown } | null)?.code;
  return (typeof code === 'string' && MESSAGES[code]) || fallback;
}

/** Change an unconfirmed invoice's destination. Refusals carry the codes above. */
export function setVendorInvoiceDestination(id: string, destination: VendorInvoiceDestination) {
  return put<{ success: boolean; data: { destination: VendorInvoiceDestination } }>(`/vendor-invoices/${id}/destination`, {
    destination,
  });
}
